/**
 * NoteNode Component
 *
 * Resizable text node with markdown support for the reference library.
 * - Edit mode (selected): Shows raw markdown textarea
 * - View mode (not selected): Shows rendered markdown
 * - Resizable via corner handles
 * - Color customization support
 */

import { memo, useCallback, useState, useEffect, useRef, CSSProperties } from 'react';
import { Handle, Position, NodeProps, NodeResizeControl } from '@xyflow/react';
import ReactMarkdown from 'react-markdown';
import { NoteNodeData } from '@/types';
import { useProjectStore } from '@/stores/projectStore';

const NoteNode = memo(function NoteNode({ id, data, selected }: NodeProps) {
  const nodeData = data as NoteNodeData;
  const updateNoteNode = useProjectStore((state) => state.updateNoteNode);
  const updateNoteSize = useProjectStore((state) => state.updateNoteSize);
  const selectNoteNode = useProjectStore((state) => state.selectNoteNode);
  const clearReferenceSelection = useProjectStore((state) => state.clearReferenceSelection);
  const saveHistory = useProjectStore((state) => state.saveHistory);

  const [hasStartedResize, setHasStartedResize] = useState(false);
  const [localContent, setLocalContent] = useState(nodeData.content || '');
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Save local content to store with debouncing
  useEffect(() => {
    // Don't save if not selected
    if (!selected) {
      return;
    }

    // Don't save if content matches what's in store
    if (localContent === nodeData.content) {
      return;
    }

    // Clear any existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Set new timeout to save after 300ms
    updateTimeoutRef.current = setTimeout(() => {
      updateNoteNode(id, { content: localContent });
      updateTimeoutRef.current = null;
    }, 300);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [localContent, selected, id, updateNoteNode, nodeData.content]);

  // Sync from store when node data changes externally (e.g., undo/redo)
  // But NOT while we're typing (have pending update)
  useEffect(() => {
    if (updateTimeoutRef.current === null && nodeData.content !== localContent) {
      setLocalContent(nodeData.content || '');
    }
  }, [nodeData.content, localContent]);

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------

  const handleResizeStart = useCallback(() => {
    if (!hasStartedResize) {
      saveHistory();
      setHasStartedResize(true);
    }
  }, [hasStartedResize, saveHistory]);

  const handleResizeEnd = useCallback(() => {
    setHasStartedResize(false);
  }, []);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Update local state immediately for smooth typing
    setLocalContent(e.target.value);
  }, []);

  const handleContainerMouseDown = useCallback(() => {
    // Select this note and clear reference selection
    selectNoteNode(id);
    clearReferenceSelection();
  }, [id, selectNoteNode, clearReferenceSelection]);

  const handleTextareaMouseDown = useCallback((e: React.MouseEvent) => {
    // Stop propagation to prevent canvas pan when interacting with textarea
    e.stopPropagation();
  }, []);

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--nb-bg-primary)',
    border: selected
      ? '2px solid var(--nb-node-selected)'
      : nodeData.color
        ? `2px solid ${nodeData.color}`
        : '2px solid var(--nb-border-secondary)',
    borderRadius: 'var(--nb-radius-md)',
    overflow: 'hidden',
    boxShadow: selected
      ? '0 4px 12px rgba(0,0,0,0.2)'
      : '0 2px 6px rgba(0,0,0,0.1)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  const headerStyles: CSSProperties = {
    padding: 'var(--nb-space-sm) var(--nb-space-md)',
    backgroundColor: nodeData.color
      ? `${nodeData.color}15` // 15 = ~9% opacity in hex
      : 'var(--nb-bg-secondary)',
    borderBottom: selected
      ? '1px solid var(--nb-node-selected)'
      : nodeData.color
        ? `1px solid ${nodeData.color}40`
        : '1px solid var(--nb-border-primary)',
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 600,
    color: 'var(--nb-text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
  };

  const textareaStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    padding: 'var(--nb-space-md)',
    fontSize: 'var(--nb-font-size-sm)',
    lineHeight: 1.6,
    color: 'var(--nb-text-primary)',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontFamily: 'inherit',
    overflow: 'auto',
  };

  const contentStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    padding: 'var(--nb-space-md)',
    fontSize: 'var(--nb-font-size-sm)',
    lineHeight: 1.6,
    color: 'var(--nb-text-primary)',
    overflow: 'auto',
    cursor: 'pointer',
  };

  const markdownStyles: CSSProperties = {
    // Markdown-specific styles
  };

  const emptyStateStyles: CSSProperties = {
    color: 'var(--nb-text-muted)',
    fontStyle: 'italic',
  };

  const resizeHandleStyle: CSSProperties = {
    width: '12px',
    height: '12px',
    backgroundColor: 'var(--nb-primary)',
    border: '2px solid white',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div style={containerStyles} onMouseDownCapture={handleContainerMouseDown}>
      {/* Hidden React Flow handles for compatibility */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />

      {/* Title header (always visible) */}
      <div style={headerStyles}>
        {nodeData.title || 'Untitled Note'}
      </div>

      {/* Content area - conditional rendering based on selection */}
      {selected ? (
        // EDIT MODE: Raw markdown textarea
        <textarea
          value={localContent}
          onChange={handleContentChange}
          onMouseDown={handleTextareaMouseDown}
          placeholder="Type your note here... (markdown supported)"
          style={textareaStyles}
          autoFocus
        />
      ) : (
        // VIEW MODE: Rendered markdown or plain text
        <div style={contentStyles}>
          {nodeData.renderMarkdown && nodeData.content ? (
            <div style={markdownStyles}>
              <ReactMarkdown>{nodeData.content}</ReactMarkdown>
            </div>
          ) : nodeData.content ? (
            <div>{nodeData.content}</div>
          ) : (
            <div style={emptyStateStyles}>Empty note</div>
          )}
        </div>
      )}

      {/* Resize handles (only when selected) */}
      {selected && (
        <NodeResizeControl
          position="bottom-right"
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
          style={resizeHandleStyle}
        />
      )}

      <Handle
        type="source"
        position={Position.Right}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
    </div>
  );
});

export default NoteNode;
